import { AlpineComponent } from "alpinejs";
import {
  default as AjaxList,
  ListXDataContext as AjaxListXDataContext,
  ListOptions as AjaxListOptions
} from '../../../components/AjaxList/Classes/List';
import ListItem from "../../../components/AjaxList/Classes/Item";
import { IHistoryStateProps } from "../../../utils/historyState";

export type ListComponent = AlpineComponent<AjaxListXDataContext<ListItem> & {
  filterItems: () => void;
  filterOption: (type: string, event: Event) => void;
  filterSelectChoice: (filter: string, event: Event) => void;
  resetFilter: () => void;
}>;

export type ListOptions = AjaxListOptions & IHistoryStateProps;

const List = (options: ListOptions, filterData = {}):ListComponent => {
  const selectedItemsArray: string[] = [];

  return {
    ...AjaxList(options, filterData),

    filterItems() {
      this.filteredItems = this.items;

      if (this.filter && this.filter.data) {
        this.filteredItems = this.filteredItems.filter((item) => {
          // for each filter property
          for (const property in this.filter!.data) {
            let filterPropertyValue = this.filter!.data[property];
            let itemPropertyValue = item[property];

            // if filter data property is empty, skip it
            if ((!filterPropertyValue) ||
              (Array.isArray(filterPropertyValue) && filterPropertyValue.length === 0)
            ) {
              continue;
            }

            // Date start
            if (property == 'date_start') {
              if (itemPropertyValue) {
                const [day, month, year] = filterPropertyValue.split('.');
                const filterDate = new Date(`${year}-${month}-${day}`).getTime();

                const [itemDay, itemMonth, itemYear] = itemPropertyValue.split('.');
                const itemDate = new Date(`${itemYear}-${itemMonth}-${itemDay}`).getTime();

                if (itemDate < filterDate) {
                  return false;
                }
              }

              continue;
            }

            // Date end
            if (property == 'date_end') {
              if (itemPropertyValue) {
                const [day, month, year] = filterPropertyValue.split('.');
                const filterDate = new Date(`${year}-${month}-${day}`).getTime();

                const [itemDay, itemMonth, itemYear] = itemPropertyValue.split('.');
                const itemDate = new Date(`${itemYear}-${itemMonth}-${itemDay}`).getTime();

                if (itemDate > filterDate) {
                  return false;
                }
              }

              continue;
            }

            if (property === 'query') {
              let filterPropertyValueEscaped = filterPropertyValue.trim().replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
              let isMatch = ['title', 'description', 'states', 'postal-code', 'location'].some(itemProperty => item[itemProperty] && RegExp(filterPropertyValueEscaped, "i").test(item[itemProperty].toString()))
              if (!isMatch) {
                return false;
              }

              continue;
            }

            // if item property has objects with uids, get flat list of uids
            if (Array.isArray(itemPropertyValue) && itemPropertyValue.length > 0 && itemPropertyValue[0].uid) {
              itemPropertyValue = itemPropertyValue.map((item: {uid: any}) => item.uid);
            }

            if (Array.isArray(filterPropertyValue) && Array.isArray(itemPropertyValue)) {
              // if filter data property and item property are both arrays, check if at least one item property is in filter data property
              if (!(itemPropertyValue as Array<any>).some((value) => (filterPropertyValue as Array<any>).includes(value))) {
                return false;
              }
            } else if (Array.isArray(filterPropertyValue)) {
              // if filter data property is an array and item property is not, check if item property is in filter data property
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            } else if (Array.isArray(itemPropertyValue)) {
              // if item property is an array and filter data property is not, check if filter data property is in item property
              if (!(itemPropertyValue as Array<any>).includes(Number(filterPropertyValue))) {
                return false;
              }
            } else if (filterPropertyValue !== itemPropertyValue) {
              // if filter data property and item property are not arrays, check if they are equal
              return false;
            }
          }

          return true;
        });
      }

      if (this.pagination) {
        this.pagination.setTotalItems(this.filteredItems.length);
        this.pagination.goToPage(1);
      }
    },

    // Filter options
    filterOption(type: string, event: Event) {
      const eTarget = event.target as HTMLInputElement;
      const value = eTarget.value.toString().toLowerCase();

      if (value.length >= 2) {
        // Filter results
        const filteredResults = this.filteredItems.filter(item =>
          item[type] && item[type].toString().toLowerCase().includes(value)
        );

        // Add results to ul list
        const ulParent = eTarget.parentElement;
        if (ulParent) {
          const list = ulParent.querySelector('ul') as HTMLUListElement;
          if (list) {
            list.innerHTML = ''; // Remove all existing li elements
            list.removeAttribute('hidden'); // Remove hidden attribute from ul

            const uniqueStates = new Set<string>();
            let isFirstItem = true;

            filteredResults.forEach(item => {
              const state = item[type];
              if (state && !uniqueStates.has(state)) {
                uniqueStates.add(state);

                const li = document.createElement('li');
                li.setAttribute('aria-selected', isFirstItem ? 'true' : 'false');
                isFirstItem = false;
                li.innerHTML = state.replace(new RegExp(value, 'gi'), (match: string) => `<mark>${match}</mark>`);
                li.addEventListener('click', () => {
                  eTarget.value = state;
                  list.setAttribute('hidden', 'hidden');
                  eTarget.focus();
                  // this.filter!.data[type] = state; // Filter items
                  // this.filterItems();
                });
                list.appendChild(li);
              }
            });
          }
        }

        // Show filter rest button
        const filterReset = document.querySelector('.button--filter') as HTMLElement;
        if (filterReset) {
          filterReset.classList.remove('d-none');
        }
      }

      // Remove filter data if input is empty
      // if (value.length == 0) {
      //   delete this.filter!.data[type];
      // }
    },

    // Filter multi select
    // @ts-ignore
    filterSelectChoice(filter: string, event: Event) {
      const eTarget = event.currentTarget as HTMLInputElement;
      const choicesDropdown = eTarget.querySelector('.choices__list--dropdown') as HTMLElement;
      const choicesListMultiple = eTarget.querySelector('.choices__list--multiple') as HTMLElement;
      const selectFilter = eTarget.querySelector('.select--filter.choices__input') as HTMLSelectElement;

      if (choicesDropdown) {
        // Set active classes
        eTarget.classList.add('is-focused', 'is-open');
        choicesDropdown.classList.add('is-active');
        choicesDropdown.setAttribute('aria-expanded', 'true');

        // Remove classes if clicked outside
        document.addEventListener('click', (e) => {
          if (!eTarget.contains(e.target as Node)) {
            eTarget.classList.remove('is-focused', 'is-open');
            choicesDropdown.classList.remove('is-active');
            choicesDropdown.setAttribute('aria-expanded', 'false');
          }
        });

        // Choice items
        const choiceItems = eTarget.querySelectorAll('.choices__list .choices__item') as NodeListOf<HTMLElement>;
        if (choiceItems) {
          // Foreach items
          choiceItems.forEach((item) => {
            item.addEventListener("mouseover", (event) => {
              event.preventDefault();

              // Remove all highlighted classes
              choiceItems.forEach((item) => {
                item.classList.remove('is-highlighted');
              });
              // Add highlighted class to the hovered item
              item.classList.add('is-highlighted');
            });

            // On choice click
            item.addEventListener('click', (event) => {
              event.preventDefault();

              const itemId = item.getAttribute('data-id') || '';
              const itemValue = item.getAttribute('data-value') || '';

              // Remove all highlighted classes
              choiceItems.forEach((item) => {
                item.classList.remove('is-highlighted');
              });

              // Hide selected choice from list
              const choice = eTarget.querySelector(`.choices__list__select_options > .choices__item[data-id="${itemId}"]`) as HTMLElement;
              if (choice) {
                choice.classList.add('d-none');
              }

              // Add selected item tags
              if (choicesListMultiple) {
                const existingChoice = choicesListMultiple.querySelector(`.choices__item[data-id="${itemId}"]`);
                if (!existingChoice) {
                  const choiceDiv = document.createElement('div');
                  choiceDiv.className = 'choices__item choices__item--selectable';
                  choiceDiv.setAttribute('data-item', '');
                  choiceDiv.setAttribute('data-id', itemId);
                  choiceDiv.setAttribute('data-value', itemValue);
                  choiceDiv.setAttribute('data-deletable', '');
                  choiceDiv.setAttribute('aria-selected', 'true');
                  choiceDiv.innerHTML = `${itemValue}<button type="button" class="choices__button" data-button="" aria-label="Remove item: '${itemValue}'">Remove item</button>`;
                  choicesListMultiple.appendChild(choiceDiv);
                }
              }

              // Add selected choices to the select input
              if (selectFilter) {
                const existingOption = selectFilter.querySelector(`option[value="${itemValue}"]`);
                if (!existingOption) {
                  const option = document.createElement('option');
                  option.value = itemValue;
                  option.text = itemValue;
                  option.setAttribute('selected', 'selected');
                  selectFilter.appendChild(option);
                }
              }

              // Filter items
              if (!selectedItemsArray.includes(itemValue)) {
                selectedItemsArray.push(itemValue);
              }
              //this.filter!.data[filter] = selectedItemsArray;
              //this.filterItems();

              // Show filter rest button
              const filterReset = document.querySelector('.button--filter') as HTMLElement;
              if (filterReset) {
                filterReset.classList.remove('d-none');
              }
            });
          });

          // Remove selected choice
          if (choicesListMultiple) {
            const selectedChoices = choicesListMultiple.querySelectorAll('.choices__item') as NodeListOf<HTMLElement>;
            if (selectedChoices) {
              selectedChoices.forEach((item) => {
                item.addEventListener('click', (event) => {
                  const itemId = item.getAttribute('data-id') || '';
                  const itemValue = item.getAttribute('data-value') || '';

                  // Remove selected choice from list
                  const choice = eTarget.querySelector(`.choices__list__select_options > .choices__item[data-id="${itemId}"]`) as HTMLElement;
                  if (choice) {
                    choice.classList.remove('d-none');
                  }

                  // Remove selected choice from tags
                  if (choicesListMultiple) {
                    const existingOption = choicesListMultiple.querySelector(`.choices__item[data-id="${itemId}"]`);
                    if (existingOption) {
                      existingOption.remove();
                    }
                  }

                  // Remove selected choice from select input
                  if (selectFilter) {
                    const existingOption = selectFilter.querySelector(`option[value="${itemValue}"]`);
                    if (existingOption) {
                      existingOption.remove();
                    }
                  }

                  // Remove item from filter
                  selectedItemsArray.splice(selectedItemsArray.indexOf(itemValue), 1);
                  if (selectedItemsArray.length === 0) {
                    //delete this.filter!.data[filter];

                    // Hide filter rest button
                    const filterReset = document.querySelector('.button--filter') as HTMLElement;
                    if (filterReset) {
                      filterReset.classList.add('d-none');
                    }
                  } else {
                    //this.filter!.data[filter] = selectedItemsArray;
                  }
                  //this.filterItems();

                  event.preventDefault();
                });
              });
            }
          }
        }
      }
    },

    resetFilter() {
      // Remove all parameters from url
      const url = new URL(window.location.href);
      url.search = '';
      window.location.href = url.toString();
    }
  }
}

export default List;
